import { useMemo, useState } from "react";
import { useCrm } from "@/lib/crm";
import { dateIt } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CalendarClock } from "lucide-react";
import { AttivitaDialog } from "./AttivitaDialog";

export function AttivitaPanel({ fixedSsd, canInvite }) {
  const { attivita, api, refresh } = useCrm();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const list = useMemo(
    () => attivita.filter((a) => a.ssd_id === fixedSsd.id).sort((a, b) => (a.data || "").localeCompare(b.data || "")),
    [attivita, fixedSsd.id]
  );

  const toggle = async (a) => {
    await api.updateAttivita(a.id, { stato: a.stato === "fatto" ? "da_fare" : "fatto" });
    await refresh();
  };
  const del = async (a) => {
    await api.deleteAttivita(a.id);
    await refresh();
    toast.success("Attività eliminata");
  };
  const save = async (data, opts = {}) => {
    let saved;
    if (editing) {
      saved = await api.updateAttivita(editing.id, data);
      toast.success("Attività aggiornata");
    } else {
      saved = await api.createAttivita({ ...data, ssd_id: fixedSsd.id, ssd_tipo: fixedSsd.tipo, ssd_nome: fixedSsd.nome });
      toast.success("Attività aggiunta");
    }
    if (opts.invite) {
      try {
        const r = await api.inviaInvito(saved.id);
        toast.success(`Invito inviato a ${r.inviati} partecipanti`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Invio invito non riuscito");
      }
    }
    setOpen(false);
    setEditing(null);
    await refresh();
  };

  return (
    <div className="space-y-2.5" data-testid="attivita-panel">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-400">Attività pianificate</span>
        <Button size="sm" variant="outline" className="rounded-full h-8 gap-1.5" data-testid="add-attivita-button"
          onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="h-3.5 w-3.5" /> {canInvite ? "Nuovo evento / invito" : "Aggiungi"}
        </Button>
      </div>

      {list.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 p-4 text-center text-xs text-slate-400">
          Nessuna attività pianificata
        </div>
      )}

      {list.map((a) => (
        <div key={a.id} data-testid={`attivita-row-${a.id}`}
          className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-900">
          <Checkbox checked={a.stato === "fatto"} onCheckedChange={() => toggle(a)} data-testid={`attivita-toggle-${a.id}`} />
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium truncate ${a.stato === "fatto" ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"}`}>{a.titolo}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="text-[10px] h-5 border-slate-200 dark:border-slate-700">{a.tipo}</Badge>
              {a.invito_inviato_at && <Badge data-testid={`attivita-invitato-${a.id}`} variant="outline" className="text-[10px] h-5 border-emerald-200 text-emerald-700">Invitato</Badge>}
              <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />{dateIt(a.data)}{a.ora ? ` · ${a.ora}` : ""}</span>
            </div>
          </div>
          <button onClick={() => { setEditing(a); setOpen(true); }} className="text-slate-300 hover:text-slate-700 dark:hover:text-white transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={() => del(a)} className="text-slate-300 hover:text-destructive transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}

      <AttivitaDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} onSave={save} initial={editing} fixedSsd={fixedSsd} canInvite={canInvite} />
    </div>
  );
}
